import { isAxiosError } from "axios";
import { toast } from "sonner";

import useAuthStore from "@/store/auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";

import { usePrivateAxios } from "./use-private-axios";

interface UseAuthenticatedQueryOptions {
	requireAuth?: boolean;
	retry?: boolean;
}

export function useAuthenticatedQuery<T>(
	key: string[],
	fn: () => Promise<T>,
	options: UseAuthenticatedQueryOptions = {}
) {
	const { isAuthenticated } = useAuthStore();

	return useQuery({
		queryKey: key,
		queryFn: async () => {
			try {
				return await fn();
			}
			catch (error) {
				if (isAxiosError(error) && error.response?.status === 401) {
					const refreshed = true;
					if (refreshed) {
						return await fn();
					}
				}
				throw error;
			}
		},
		enabled: !options.requireAuth || isAuthenticated,
		retry: options.retry
	});
}

export function useUpdateLoginPin() {
	const privateAxios = usePrivateAxios();
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["update-login-pin"],
		mutationFn: async (pin: string) => {
			const { data } = await privateAxios.post("/auth/set-login-pin", { pin });
			return data;
		},
		onSuccess() {
			toast.success("Login pin updated successfully");
		},
		onError: () => toast.error("Failed to update login pin"),
		onSettled: () => queryClient.invalidateQueries({ queryKey: ["user-login-pin"] })
	});
}

export function usePinQuery() {
	const privateAxios = usePrivateAxios();

	return useQuery({
		queryKey: ["user-login-pin"],
		queryFn: async () => {
			const { data } = await privateAxios.get("/auth/check-pin");
			return data;
		}
	});
}

export function useDeleteUser() {
	const privateAxios = usePrivateAxios();

	const router = useRouter();

	const logout = useAuthStore(state => state.logout);
	return useMutation({
		mutationKey: ["delete-user"],
		mutationFn: async (userId: string) => {
			const { data } = await privateAxios.delete(`/auth/delete/${userId}`);
			return data;
		},
		onSuccess: () => {
			logout();
			router.invalidate();
		}
	});
}

export function useUpdateProfilePicture() {
	const privateAxios = usePrivateAxios();
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["update-profile-picture"],
		mutationFn: async (data: FormData) => {
			const { data: res } = await privateAxios.put("/auth/update_profile_image", data, {
				headers: {
					"Content-Type": "multipart/form-data"
				}
			});
			return res;
		},
		onSettled: () => queryClient.invalidateQueries({ queryKey: ["user-profile"] })
	});
}
